import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BoltIcon,
  SignalIcon,
  ChartBarIcon,
  ListBulletIcon,
  UserIcon,
  UsersIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import AdminStats from "./AdminStats";
import EventsList from "./EventsList";
import UserAnalytics from "./UserAnalytics";
import AllUsersAnalytics from "./AllUsersAnalytics";
import AnnouncementsAnalytics from "./AnnouncementsAnalytics";
import RequestsMonitor from "./monitoring/RequestsMonitor";
import LiveFeed from "./monitoring/LiveFeed";

const GROUPS = [
  {
    label: "Сервер",
    tabs: [
      { key: "requests", label: "Запросы API", icon: BoltIcon, hint: "Скорость и ошибки backend по эндпоинтам", Component: RequestsMonitor },
      { key: "live", label: "Живой поток", icon: SignalIcon, hint: "Запросы и события в реальном времени", Component: LiveFeed },
    ],
  },
  {
    label: "Поведение",
    tabs: [
      { key: "stats", label: "Статистика событий", icon: ChartBarIcon, hint: "Что делают пользователи в приложении и на сайте", Component: AdminStats },
      { key: "events", label: "Журнал событий", icon: ListBulletIcon, hint: "Отдельные события с фильтрами", Component: EventsList },
      { key: "user-analytics", label: "Один пользователь", icon: UserIcon, hint: "История действий конкретного пользователя", Component: UserAnalytics },
    ],
  },
  {
    label: "Бизнес",
    tabs: [
      { key: "all-users-analytics", label: "Пользователи", icon: UsersIcon, hint: "Регистрации, активность, типы аккаунтов", Component: AllUsersAnalytics },
      { key: "announcements-analytics", label: "Объявления", icon: DocumentTextIcon, hint: "Публикации, модерация, категории", Component: AnnouncementsAnalytics },
    ],
  },
];

const ALL_TABS = GROUPS.flatMap((g) => g.tabs);

const MonitoringDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const current = useMemo(() => {
    const key = new URLSearchParams(location.search).get("tab");
    return ALL_TABS.find((t) => t.key === key) || ALL_TABS[0];
  }, [location.search]);
  const Active = current.Component;

  const select = (key) => {
    const p = new URLSearchParams(location.search);
    p.set("tab", key);
    navigate({ pathname: location.pathname, search: `?${p.toString()}` }, { replace: true });
  };

  return (
    <div className="space-y-4 text-left">
      <div>
        <h2 className="text-2xl font-bold text-text-primary">Мониторинг</h2>
        <p className="mt-0.5 text-sm text-text-secondary">{current.hint}</p>
      </div>

      <div role="tablist" className="flex gap-6 overflow-x-auto border-b border-border-primary">
        {GROUPS.map((g) => (
          <div key={g.label} className="flex shrink-0 items-end gap-1">
            <span className="mb-3 mr-1 text-[10px] font-semibold uppercase tracking-wider text-text-muted">{g.label}</span>
            {g.tabs.map((t) => {
              const Icon = t.icon;
              const active = t.key === current.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => select(t.key)}
                  className={`-mb-px flex items-center gap-1.5 whitespace-nowrap border-b-2 px-2.5 py-2.5 text-sm font-medium transition-colors ${
                    active ? "border-[#4F45E5] text-text-primary" : "border-transparent text-text-secondary hover:text-text-primary"
                  }`}
                >
                  <Icon className={`h-4 w-4 ${active ? "text-[#7C3AED]" : ""}`} />
                  {t.label}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      <div role="tabpanel">
        <Active key={current.key} />
      </div>
    </div>
  );
};

export default MonitoringDashboard;
